import { hasViewAccessToResource } from "api/general";
import { useTiers } from "api/tiers";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { CustomAttributes } from "components/CustomAttributes/CustomAttributes";
import {
  DynamicSearch,
  DynamicSearchType,
} from "components/FiltersV2/DynamicSearch";
import { FiltersV2 } from "components/FiltersV2/Filters";
import {
  getNewFiltersOnly,
  parseFiltersForBE,
} from "components/FiltersV2/utils";
import { Subheader } from "components/Header/Subheader/Subheader";
import { SubheaderActionButton } from "components/Header/Subheader/SubheaderActionButton";
import { SubheaderWrapper } from "components/Header/Subheader/SubheaderWrapper";
import { TopHeader } from "components/Header/TopHeader/TopHeader";
import { createHeadItems, OLTable } from "components/Table/OLTable";
import { StatusValue } from "components/Table/StatusValue";
import { calculateCount } from "components/Table/tableHelpers";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { clearTablePage, selectTables } from "reduxState/tables/tablesSlice";
import { useTablesStateHelpers } from "state/tables/tableHelpers";
import { TIERSET_LIST_ID } from "state/tables/types";
import {
  SelectedFilter,
  SelectedFilter as SelectedFilterV2,
} from "types/filters";
import { ListingActions, ListResponse } from "types/listing";
import { links } from "utilities/links";
import { getDowngradeLabel } from "../TierSet/utils";

export const TierSetList = () => {
  const { message } = useMessage();
  const [dynamicSearch, setDynamicSearch] = useState<DynamicSearchType>();
  const { listing, updateListing } = useListing(TIERSET_LIST_ID);
  const appDispatch = useAppDispatch();
  const tables = useAppSelector(selectTables);
  const hasViewTierPermission = hasViewAccessToResource("LEVEL");
  const {
    setPersistentFilters,
    setPersistentListingSorting,
    setColumnOrdering,
  } = useTablesStateHelpers();
  const [filters, setFilters] = useState<SelectedFilter[]>(
    tables[TIERSET_LIST_ID]?.filters?.filter(
      (item: SelectedFilter) => item.type !== "dynamic-search",
    ) || [],
  );
  const { useListOfTierSets } = useTiers();
  const { data: tierSetList, error } = useListOfTierSets({
    filters: parseFiltersForBE(getNewFiltersOnly(filters)),
    order: listing.order || "asc",
    page: listing.page,
    rows: listing.rowsPerPage,
    sortKey: listing.property || "name",
    dynamicSearch,
  }) as { data: ListResponse; error?: any; isValidating?: boolean };

  const navigate = useNavigate();

  useEffect(() => {
    const isError = typeof error !== "undefined";
    // Save filters only when data is correct and error not occurs
    if (!isError && tierSetList) {
      setPersistentFilters(filters, TIERSET_LIST_ID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, tierSetList, error]);

  const items = useMemo(() => {
    return tierSetList?.items.map((item) => ({
      cells: [
        { content: item.name, property: "name" },
        {
          content: <StatusValue status={item.active} />,
          property: "status",
        },
        {
          content: item.tiers?.length || "-",
          property: "numberOfTiers",
        },
        {
          content: message({ id: getDowngradeLabel(item.downgrade) }),
          property: "downgradeOption",
        },
        {
          content: <CustomAttributes attributes={item.labels} />,
          property: "atributes",
        },
      ],
      id: item.tierSetId,
    }));
  }, [tierSetList, message]);
  const setSelectedFilters = (filters: SelectedFilterV2[]) => {
    listing.page = 1;
    setPersistentListingSorting(
      listing.order,
      listing.property,
      listing.rowsPerPage,
      1,
      TIERSET_LIST_ID,
    );

    setFilters(filters);
  };

  const headItems = createHeadItems([
    {
      headerText: message({ id: "name" }),
      property: "name",
      isSortable: true,
    },
    {
      headerText: message({ id: "status" }),
      property: "status",
      isSortable: false,
    },
    {
      align: "right",
      headerText: message({ id: "numberOfTiers" }),
      property: "numberOfTiers",
      isSortable: false,
    },
    {
      headerText: message({ id: "downgradeOption" }),
      property: "downgrade",
      isSortable: false,
    },
    {
      headerText: message({ id: "customAttributes" }),
      property: "attributes",
      isSortable: false,
    },
  ]);

  return (
    <>
      <TopHeader title={message({ id: "listOfTierSets" })} />
      <Subheader>
        <FiltersV2
          data-test-id="add-filter"
          properties={[
            {
              query: "active",
              label: message({ id: "status" }),
              type: "boolean-status",
            },
            {
              query: "description",
              label: message({ id: "description" }),
              type: "text",
            },
            {
              query: "name",
              label: message({ id: "name" }),
              type: "tier-name-filter",
            },
          ]}
          setFilters={setSelectedFilters}
          savedFilters={getNewFiltersOnly(filters)}
          total={tierSetList?.total}
          donutLabel={message({ id: "tiers" })}
        />
        <SubheaderActionButton
          text={message({ id: "newTierSet" })}
          onClick={() => {
            return navigate(links.addTierSet);
          }}
          variant="contained"
          disabled={hasViewTierPermission}
          data-test-id="tiers-add-tier-add-tier-button"
        />
      </Subheader>
      <Subheader>
        <SubheaderWrapper alignRight>
          <DynamicSearch
            property="name"
            setDynamicSearch={setDynamicSearch}
            onSearch={() =>
              appDispatch(clearTablePage({ tableName: TIERSET_LIST_ID }))
            }
          />
        </SubheaderWrapper>
      </Subheader>
      <OLTable
        fullView
        setColumnOrdering={setColumnOrdering}
        tableName={TIERSET_LIST_ID}
        data-test-id="tierset"
        onRowClick={(id: string) => navigate(links.tierSet(id as string))}
        sort={{
          order: listing.order || "asc",
          property: listing.property || "name",
          rowsPerPage: listing.rowsPerPage,
          page: listing.page,
        }}
        actions={{
          body: [
            {
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => navigate(links.tierSet(id as string)),
              label: message({ id: "editTiersSet" }),
              disableCondition: () => hasViewTierPermission,
            },
          ],
        }}
        columns={tierSetList && headItems}
        rows={tierSetList && items}
        onSort={(prop) =>
          updateListing({
            payload: prop,
            type: ListingActions.SET_SORT,
          })
        }
        pagination={{
          count: calculateCount(
            tierSetList?.total.filtered,
            tierSetList?.total.all,
          ),
          rowsPerPage: listing.rowsPerPage,
          page: listing.page,
          onChangePage: (page) => {
            updateListing({ type: ListingActions.CHANGE_PAGE, payload: page });
          },
          onChangeRowsPerPage: (rows) => {
            updateListing({
              type: ListingActions.CHANGE_ROWS_PER_PAGE,
              payload: rows,
            });
          },
        }}
      />
    </>
  );
};
